/**
 * Package Pricing Constants
 * Hard-coded pricing tables for photo-only and photo+video packages
 * DO NOT modify these values without approval - they are exact pricing tiers
 */

// Photo-only package pricing (exact values, not calculated)
export const PHOTO_PACKAGES = [
  { hours: 1, price: 550 },
  { hours: 2, price: 650 },
  { hours: 3, price: 700 },
  { hours: 4, price: 750 },
  { hours: 5, price: 800 },
  { hours: 6, price: 850 },
  { hours: 7, price: 900 },
  { hours: 8, price: 950 },
  { hours: 9, price: 1150 },
  { hours: 10, price: 1250 },
  { hours: 11, price: 1350 },
  { hours: 12, price: 1450 }
]

// Video-only package pricing (exact values, not calculated)
export const VIDEO_ONLY_PACKAGES = [
  { hours: 1, price: 850 },
  { hours: 2, price: 1050 },
  { hours: 3, price: 1250 },
  { hours: 4, price: 1450 },
  { hours: 5, price: 1650 },
  { hours: 6, price: 1850 },
  { hours: 7, price: 2050 },
  { hours: 8, price: 2250 },
  { hours: 9, price: 2450 },
  { hours: 10, price: 2650 },
  { hours: 11, price: 2850 },
  { hours: 12, price: 3250 }
]

// Photo+Video bundle pricing (exact values, not calculated)
export const PHOTO_VIDEO_PACKAGES = [
  { hours: 1, price: 1250 },
  { hours: 2, price: 1450 },
  { hours: 3, price: 1650 },
  { hours: 4, price: 1850 },
  { hours: 5, price: 2050 },
  { hours: 6, price: 2250 },
  { hours: 7, price: 2450 },
  { hours: 8, price: 2650 },
  { hours: 9, price: 2850 },
  { hours: 10, price: 3050 },
  { hours: 11, price: 3250 },
  { hours: 12, price: 3650 }
]

// Add-on pricing
export const ADDONS_PRICING = {
  INSURED_PHOTOGRAPHER: {
    id: 'insured_photographer',
    title: 'Insured Photographer',
    price: 395,
    description: '94% of couples choose this',
    recommended: true
  },
  DATE_CHANGE: {
    id: 'date_change',
    title: 'One-Time Date Change',
    price: 195,
    description: '72% of couples choose this',
    recommended: true
  },
  SECOND_PHOTOGRAPHER: {
    id: 'second_photographer',
    title: 'Second Photographer',
    hourlyRate: 100,
    description: '$100/hour - matches package duration',
    requiresHours: true,
    minimumHours: 3, // Must be MORE than 3 hours (so 4+ hours)
    eligiblePackageTypes: ['photo', 'photoOnly', 'photoVideo']
  },
  SECOND_VIDEOGRAPHER: {
    id: 'second_videographer',
    title: 'Second Videographer',
    hourlyRate: 135,
    description: '$135/hour - matches package duration',
    requiresHours: true,
    minimumHours: 3, // Must be MORE than 3 hours (so 4+ hours)
    eligiblePackageTypes: ['videoOnly', 'photoVideo']
  },
  RAW_FOOTAGE: {
    id: 'raw_footage',
    title: 'Raw Footage',
    price: 395,
    description: '81% of couples choose this',
    recommended: true,
    requiresVideo: true // Only show if video was selected
  },
  RUSH_DELIVERY: {
    id: 'rush_delivery',
    title: 'Rush Delivery',
    price: 650,
    description: 'Priority editing and expedited delivery'
  },
  ENGAGEMENT_SESSION: {
    id: 'engagement_session',
    title: 'Engagement, Bridal, or Anniversary Session',
    price: 750,
    description: '2-3 hour session with 100+ edited photos'
  },
  HOUR_ON_US: {
    id: 'hour-on-us',
    title: 'Hour on Us',
    price: 0
  },
  GETTING_READY_PHOTOS: {
    id: 'getting-ready-photos',
    title: 'Getting Ready Photos',
    price: 395,
    description: 'Capture the morning-of moments'
  }
}

/**
 * Get photo-only package price by hours
 * @param {number} hours - Number of hours (1-12)
 * @returns {number|null} Price in dollars, or null if invalid
 */
export const getPhotoPackagePrice = (hours) => {
  const pkg = PHOTO_PACKAGES.find(p => p.hours === hours)
  return pkg ? pkg.price : null
}

/**
 * Get video-only package price by hours
 * @param {number} hours - Number of hours (1-12)
 * @returns {number|null} Price in dollars, or null if invalid
 */
export const getVideoOnlyPackagePrice = (hours) => {
  const pkg = VIDEO_ONLY_PACKAGES.find(p => p.hours === hours)
  return pkg ? pkg.price : null
}

/**
 * Get photo+video bundle price by hours
 * @param {number} hours - Number of hours (1-12)
 * @returns {number|null} Price in dollars, or null if invalid
 */
export const getPhotoVideoPackagePrice = (hours) => {
  const pkg = PHOTO_VIDEO_PACKAGES.find(p => p.hours === hours)
  return pkg ? pkg.price : null
}

/**
 * Get all available photo packages for display
 * @returns {Array} Array of package objects with hours and price
 */
export const getAllPhotoPackages = () => {
  return [...PHOTO_PACKAGES]
}

/**
 * Get all available video-only packages for display
 * @returns {Array} Array of package objects with hours and price
 */
export const getAllVideoOnlyPackages = () => {
  return [...VIDEO_ONLY_PACKAGES]
}

/**
 * Get all available photo+video packages for display
 * @returns {Array} Array of package objects with hours and price
 */
export const getAllPhotoVideoPackages = () => {
  return [...PHOTO_VIDEO_PACKAGES]
}

/**
 * Calculate second photographer price
 * Flat rate: $100/hour, requires more than 3 hours (4+ hours)
 * @param {number} hours - Number of hours
 * @returns {number|null} Price in dollars, or null if invalid hours
 */
export const getSecondPhotographerPrice = (hours) => {
  if (!hours || hours <= 3) return null // Requires events longer than 3 hours
  return hours * ADDONS_PRICING.SECOND_PHOTOGRAPHER.hourlyRate
}

/**
 * Calculate second videographer price
 * Flat rate: $135/hour, requires more than 3 hours (4+ hours)
 * @param {number} hours - Number of hours
 * @returns {number|null} Price in dollars, or null if invalid hours
 */
export const getSecondVideographerPrice = (hours) => {
  if (!hours || hours <= 3) return null // Requires events longer than 3 hours
  return hours * ADDONS_PRICING.SECOND_VIDEOGRAPHER.hourlyRate
}

/**
 * Validate if hours are valid for packages
 * @param {number} hours - Number of hours to validate
 * @returns {boolean} True if valid hours (1-12)
 */
export const isValidPackageHours = (hours) => {
  return Number.isInteger(hours) && hours >= 1 && hours <= 12
}

/* ------------------------------------------------------------------------------------------------
 * Single source of truth (20 Sep 2026). Until today the same three ladders lived in five files:
 * this one, constants/pricing.js, the homepage widget, Grace, and express checkout. They agreed by
 * luck. Everything below used to be in constants/pricing.js; the pages that imported it now import
 * here, and the server prices a booking from these tables instead of trusting the browser.
 * ---------------------------------------------------------------------------------------------- */

export const MAX_PACKAGE_HOURS = 12
export const PACKAGE_TYPES = ['photo', 'photoVideo', 'videoOnly']

const toMap = list => Object.fromEntries(list.map(p => [p.hours, p.price]))
/** Hour → dollars maps, the shape the checkout pages read. Derived from the arrays above, never edited. */
export const BASE_PHOTO_PRICES = toMap(PHOTO_PACKAGES)
export const VIDEO_ONLY_PRICES = toMap(VIDEO_ONLY_PACKAGES)
export const PHOTO_VIDEO_PRICES = toMap(PHOTO_VIDEO_PACKAGES)

/** 'Photography' / 'photoOnly' / 'photo' → 'photo'; 'Photo + Video' / 'bundle' / 'photoAndVideo' → 'photoVideo'; 'Videography' / 'video' → 'videoOnly'. */
export function normalizePackageType(value) {
  const s = String(value || '').toLowerCase().replace(/[^a-z]/g, '')
  if (!s) return null
  if (s.includes('photo') && s.includes('video')) return 'photoVideo'
  if (s === 'bundle') return 'photoVideo'
  if (s.includes('video')) return 'videoOnly'
  if (s.includes('photo')) return 'photo'
  return null
}

export function ladderFor(packageType) {
  const t = normalizePackageType(packageType)
  if (t === 'photo') return PHOTO_PACKAGES
  if (t === 'videoOnly') return VIDEO_ONLY_PACKAGES
  if (t === 'photoVideo') return PHOTO_VIDEO_PACKAGES
  return null
}

/**
 * Custom photo counts (Sales Playbook, Traci, updated 16 Jul 2026; wired 21 Sep 2026).
 * Every size includes 50 edited photos per hour. Photo and Photo + Video packages can carry more, in steps of
 * ten photos at $50 a step, up to the playbook's cap for that size (the 12-hour caps land exactly on $5,000).
 * Video-only has no photo count. Nothing here is derived from display logic: the caps are the playbook's table.
 */
export const PHOTOS_PER_HOUR = 50
export const PHOTO_STEP = 10
export const PHOTO_STEP_PRICE = 50
export const MAX_PHOTOS = {
  photo: { 1: 50, 2: 100, 3: 150, 4: 200, 5: 250, 6: 300, 7: 350, 8: 430, 9: 460, 10: 510, 11: 560, 12: 1310 },
  photoVideo: { 1: 80, 2: 130, 3: 180, 4: 230, 5: 280, 6: 330, 7: 380, 8: 430, 9: 480, 10: 530, 11: 610, 12: 870 }
}

/** Photos included at the base price for a size. */
export function basePhotosFor(hours) {
  const h = Math.round(Number(hours))
  return Number.isFinite(h) && h >= 1 && h <= MAX_PACKAGE_HOURS ? h * PHOTOS_PER_HOUR : null
}

/**
 * Photos a booking delivers now (Traci's doc, Oct 2026: "when someone adds coverage the number of photos must update
 * as well; 6 hours should be 300 photos", and every booking shows its count). The count stored at booking (or the
 * standard 50 an hour for the hours booked then, when none was stored), plus 50 for each hour bought later. The free
 * getting-ready hour is in hours_booked at checkout but not in package.hoursBooked, so it adds none. Video only: null.
 */
export function deliverablePhotos(booking) {
  if (normalizePackageType(booking?.package_type) === 'videoOnly') return null
  const pd = booking?.personalization_data || {}
  const pkg = pd.package || {}
  const now = Number(booking?.hours_booked) || 0
  const atBooking = Number(pkg.hoursBooked) || now
  const stored = Number(pkg.photos ?? pd.photos)
  const base = Number.isFinite(stored) && stored > 0 ? stored : basePhotosFor(atBooking)
  if (!base) return null
  return base + PHOTOS_PER_HOUR * Math.max(0, Math.round(now - atBooking))
}

/** The most photos the playbook sells at a size, or null for video-only / unknown sizes. */
export function maxPhotosFor(packageType, hours) {
  const type = normalizePackageType(packageType)
  const h = Math.round(Number(hours))
  if (!type || type === 'videoOnly' || !MAX_PHOTOS[type]) return null
  return MAX_PHOTOS[type][h] ?? null
}

/** Every photo count the playbook sells at a size, with its price. Video-only: one option, no photo count. */
export function photoOptionsFor(packageType, hours) {
  const base = packagePriceFor(packageType, hours)
  if (base === null) return []
  const basePhotos = basePhotosFor(hours)
  const max = maxPhotosFor(packageType, hours)
  if (max === null) return [{ photos: null, price: base, extra: 0 }]
  const out = []
  for (let p = basePhotos; p <= max; p += PHOTO_STEP) out.push({ photos: p, price: base + ((p - basePhotos) / PHOTO_STEP) * PHOTO_STEP_PRICE, extra: p - basePhotos })
  return out
}

/**
 * Is this photo count one the playbook sells at this size? `photos` may be empty (the base). Video-only ignores
 * the count. Returns the count the booking should carry (null = base, nothing to record).
 * @returns {{ ok: true, photos: number | null, extra: number } | { ok: false, reason: string }}
 */
export function validatePhotoCount(packageType, hours, photos) {
  const type = normalizePackageType(packageType)
  if (packagePriceFor(type, hours) === null) return { ok: false, reason: 'No package at that size' }
  const max = maxPhotosFor(type, hours)
  if (photos === undefined || photos === null || photos === '' || max === null) return { ok: true, photos: null, extra: 0 }
  const p = Math.round(Number(photos))
  const basePhotos = basePhotosFor(hours)
  if (!Number.isFinite(p)) return { ok: false, reason: 'Photo count must be a number' }
  if (p === basePhotos) return { ok: true, photos: null, extra: 0 }
  if (p < basePhotos) return { ok: false, reason: `${hours} hours includes ${basePhotos} photos; a smaller count is not sold` }
  if (p > max) return { ok: false, reason: `${hours} hours of ${type === 'photo' ? 'photo' : 'photo + video'} coverage goes up to ${max} photos` }
  if ((p - basePhotos) % PHOTO_STEP !== 0) return { ok: false, reason: `Photo counts step by ${PHOTO_STEP}` }
  return { ok: true, photos: p, extra: p - basePhotos }
}

/**
 * The package price in dollars for a type and hour count, or null when no such package exists. With a photo
 * count above the base, the playbook's $50 per ten photos is added; a count the playbook does not sell is null.
 */
export function packagePriceFor(packageType, hours, photos = null) {
  const ladder = ladderFor(packageType)
  const h = Math.round(Number(hours))
  if (!ladder || !Number.isFinite(h)) return null
  const base = ladder.find(p => p.hours === h)?.price ?? null
  if (base === null || photos === null || photos === undefined) return base
  const check = validatePhotoCount(packageType, h, photos)
  if (!check.ok) return null
  return base + (check.extra / PHOTO_STEP) * PHOTO_STEP_PRICE
}

/**
 * What the server writes as the package price. The browser's number is not trusted: a plain booking is
 * priced from the ladder, full stop. A negotiated booking (the homepage widget) may pay MORE than the
 * ladder, never less. Below the ladder is refused.
 * @returns {{ ok: true, price: number, source: 'ladder' | 'negotiated' } | { ok: false, reason: string }}
 */
export function acceptBasePrice({ offered, ladder, negotiated = false } = {}) {
  const l = Number(ladder)
  if (!Number.isFinite(l) || l <= 0) return { ok: false, reason: 'No package at that size' }
  const o = Number(offered)
  if (negotiated && Number.isFinite(o) && o >= l) return { ok: true, price: Math.round(o * 100) / 100, source: 'negotiated' }
  if (negotiated && Number.isFinite(o) && o < l) return { ok: false, reason: `Offered $${o} is below the $${l} package price` }
  return { ok: true, price: l, source: 'ladder' }
}

const rounded = hours => { const h = Math.round(Number(hours)); return hours && h >= 1 && h <= MAX_PACKAGE_HOURS ? h : null }
export function getBasePhotoPrice(hours) { const h = rounded(hours); return h ? BASE_PHOTO_PRICES[h] : null }
export function getVideoOnlyPrice(hours) { const h = rounded(hours); return h ? VIDEO_ONLY_PRICES[h] : null }
export function getPhotoVideoPrice(hours) { const h = rounded(hours); return h ? PHOTO_VIDEO_PRICES[h] : null }
/** Adding video to a photo booking: the gap between the two ladders at that hour. */
export function getVideoAddonPrice(hours) {
  const a = getBasePhotoPrice(hours), b = getPhotoVideoPrice(hours)
  return a === null || b === null ? null : b - a
}
export function formatPrice(price) {
  if (price === null || price === undefined || !Number.isFinite(price)) return '$0'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(price)
}
export function validateHours(hours) {
  if (!hours || !Number.isFinite(hours)) return { isValid: false, error: 'Invalid photo hours selected' }
  const h = Math.round(hours)
  if (h < 1 || h > MAX_PACKAGE_HOURS) return { isValid: false, error: `Photo hours must be between 1 and ${MAX_PACKAGE_HOURS} hours` }
  return { isValid: true, error: null }
}
